/* Shift-clock geolocation — shared helpers for the CRM header "Clock in"
 * button (time_clock_punches). Geolocation is MANDATORY on every shift-clock
 * event (clock in/out, lunch & break start/end, resume): the browser prompt is
 * BLOCKING client-side and the punch endpoint 400s server-side without valid
 * coords. Mirrors the subcontractor portal's requestClockInLocation pattern.
 *
 * This module is framework-agnostic so the validator can be unit-tested
 * (src/lib/__tests__/timeClockGeo.test.ts) — the regression guard for the
 * "punches must carry a location" rule.
 */

export type PunchGeo = { lat: number; lng: number; accuracy_m: number | null }

export type PunchGeoValidation =
  | { ok: true; geo: PunchGeo }
  | { ok: false; error: string }

/** Server-mirror validator: coords must be finite and in range. Accuracy is
 *  optional (stored when present, no cap). The error string matches the
 *  endpoint's 400 body. Keep in lockstep with the inline check in
 *  api/src/index.ts POST /api/crm/time-clock/punch. */
export function validatePunchGeo(lat: unknown, lng: unknown, accuracyM?: unknown): PunchGeoValidation {
  // Coerce numbers + numeric strings only. null / undefined / '' must FAIL —
  // Number(null) and Number('') are both 0, which would otherwise sneak past
  // the range check as a valid "0,0" location.
  const coerce = (v: unknown): number => {
    if (v == null) return NaN
    if (typeof v === 'number') return v
    if (typeof v === 'string') return v.trim() === '' ? NaN : Number(v)
    return NaN
  }
  const la = coerce(lat)
  const ln = coerce(lng)
  const valid =
    Number.isFinite(la) && la >= -90 && la <= 90 &&
    Number.isFinite(ln) && ln >= -180 && ln <= 180
  if (!valid) return { ok: false, error: 'Location is required to clock in/out.' }
  const accRaw = accuracyM == null ? null : Number(accuracyM)
  const accuracy_m = accRaw != null && Number.isFinite(accRaw) && accRaw >= 0 ? accRaw : null
  return { ok: true, geo: { lat: la, lng: ln, accuracy_m } }
}

/** Blocking browser geolocation. Rejects on unsupported / permission-denied /
 *  unavailable / timeout so the caller can ABORT the punch (HARD block — no
 *  best-effort fallback). enableHighAccuracy, 15s timeout. Mirrors the portal's
 *  requestClockInLocation. */
export function requestPunchLocation(): Promise<PunchGeo> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Location is required to clock in/out, but this device cannot share it.'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy_m: Number.isFinite(pos.coords.accuracy) ? Math.round(pos.coords.accuracy) : null,
      }),
      (err) => {
        const msg = err && err.code === err.PERMISSION_DENIED
          ? 'Location permission is required to clock in/out. Enable location access and try again.'
          : err && err.code === err.TIMEOUT
            ? 'Could not get your location in time. Check your signal and try again.'
            : 'Could not get your location to clock in/out. Try again.'
        reject(new Error(msg))
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    )
  })
}

/** maps.google.com link for a punch's coords, or null when no coords. */
export function punchMapsUrl(lat: number | null | undefined, lng: number | null | undefined): string | null {
  if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return `https://maps.google.com/?q=${lat},${lng}`
}

/** "±Nm" accuracy badge text, or null. */
export function punchAccuracyLabel(accuracyM: number | null | undefined): string | null {
  if (accuracyM == null || !Number.isFinite(accuracyM)) return null
  return `±${Math.round(accuracyM)}m`
}
